import { EnsApi, EnsName, EnsWallet } from '../../application/ens/ports';
import { TokenGateway } from '../../application/governance/ports';
import { fullName } from '../../domain/ens/ens';
import { Demo } from './demo';

/** Offline stand-ins for the ENS API, the wallet and the wZEUS contracts, per demo scenario. */

const ME = '0x7a3B9c2D4e5F60718293A4b5C6d7E8f901234aBc';
const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));
const pace = (demo: Demo) => (demo === 'slow' ? 2200 : 450);

const NAMES = ['zeusmaxi', 'swampking', 'pepesdogarmy', 'gm', 'diamondpaws', 'cc8', 'rainbowtongue', 'degenpup', 'anon420'];
const LONG = 'thisisaveryveryverylongnamethatneverends-zeus-cc8-army';

const name = (label: string, i: number): EnsName => ({
  label,
  name: fullName(label),
  address: ME,
  avatar: i % 3 === 0 ? undefined : '/images/zeus-avatar.jpg',
});

export function demoEnsApi(demo: Demo): EnsApi {
  const fail = async () => {
    await wait(pace(demo));
    throw new Error('Network error');
  };
  const refused = { ok: false, status: 402, error: 'Payment verification failed', details: 'This payment was already used for another name' };
  const fine = { ok: true, status: 200 };
  return {
    async isAvailable(label) {
      if (demo === 'error') return fail();
      await wait(pace(demo));
      return !NAMES.includes(label);
    },
    async mint() {
      await wait(pace(demo));
      return demo === 'fail' ? refused : fine;
    },
    async recent() {
      if (demo === 'error') return fail();
      await wait(pace(demo));
      if (demo === 'empty') return { names: [], total: 0 };
      if (demo === 'worst') return { names: [LONG, ...NAMES].map(name), total: 12345 };
      return { names: NAMES.map(name), total: 87 };
    },
    async mine() {
      if (demo === 'error') return fail();
      await wait(pace(demo));
      if (demo === 'empty' || demo === 'off') return [];
      if (demo === 'worst') return [LONG, 'gm', 'swampking', 'diamondpaws', 'zeusmaxi', 'cc8'].map(name);
      return ['zeusmaxi', 'gm'].map(name);
    },
    async ethUsd() {
      return 2500;
    },
    async uploadAvatar() {
      await wait(pace(demo));
      return '/images/zeus-avatar.jpg';
    },
    async updateAddress() {
      await wait(pace(demo));
      return demo === 'fail' ? { ...refused, status: 401, error: 'Ownership not proven', details: 'Signature expired, sign again' } : fine;
    },
    async updateAvatar() {
      await wait(pace(demo));
      return demo === 'fail' ? { ...refused, status: 401, error: 'Ownership not proven', details: 'Signature expired, sign again' } : fine;
    },
  };
}

export function demoWallet(demo: Demo): EnsWallet {
  return {
    address: demo === 'off' ? null : ME,
    async signMessage() {
      await wait(pace(demo));
      return '0xdemo';
    },
    async pay() {
      await wait(pace(demo) * 2);
      return '0xdemotx';
    },
  };
}

const UNIT = BigInt(1000000000);

export function demoGateway(demo: Demo): TokenGateway {
  const state =
    demo === 'worst'
      ? { zeus: BigInt('420690000000000') * UNIT, wzeus: BigInt('999999999999') * UNIT }
      : demo === 'empty'
        ? { zeus: BigInt(0), wzeus: BigInt(0) }
        : { zeus: BigInt(125000000) * UNIT, wzeus: BigInt(42000000) * UNIT };
  let allowance = BigInt(0);
  let delegate: string | null = demo === 'worst' ? ME : null;
  const step = async () => {
    await wait(pace(demo) * 2);
    if (demo === 'fail') throw new Error('Transaction reverted');
  };
  return {
    async read() {
      if (demo === 'error') throw new Error('Network error');
      await wait(200);
      return { ...state, allowance, decimals: 9, votes: delegate ? state.wzeus : BigInt(0), delegate };
    },
    async approve(amount) {
      await step();
      allowance = amount;
    },
    async wrap(amount) {
      await step();
      state.zeus -= amount;
      state.wzeus += amount;
      allowance = BigInt(0);
    },
    async unwrap(amount) {
      await step();
      state.wzeus -= amount;
      state.zeus += amount;
    },
    async delegateToSelf() {
      await step();
      delegate = ME;
    },
  };
}
